import {
    Drawer, 
    Menu,
    useMediaQuery, 
    useTheme,
    AppBar,
    Divider,
    IconButton,
    List,
    ListItemText,
    MenuItem,
    Toolbar,
    Typography,
    ListItemIcon,
    ListSubheader,
    ListItemButton,
} from '@mui/material';
import { 
    AccountBox,
    AccountCircle, 
    Announcement, 
    Assessment, 
    Assignment, 
    Email, 
    ExitToApp,
    Home, 
    LibraryBooks,
    Lock,
    LiveHelp, 
    SupervisorAccount,
    Explore
} from '@mui/icons-material';
import { Scrollbars } from 'react-custom-scrollbars-2';
import MenuIcon from '@mui/icons-material/Menu';
import "firebase/auth";
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Navigate } from "react-router-dom";
import styled from "styled-components";

import { loggedIn, loggedOut } from '../actions/loggedIn';
import { clearUserProfiles } from "../actions/userProfiles";
import { clearUserRole, setUserRole } from "../actions/userRole";
import { getUserRole } from '../api';
import { firebaseUser, firebaseAuth } from "../firebase";
import logo_menu from "../image/logo_menu.png";
import logo_white from "../image/logo_white.png";
import { AppState } from '../reducers';
import { isTrialAccount, RoleType } from '../types/UserRole';
import { useQueryParams } from '../helper/hooks';
import { UserValidity } from '../types/UserValidity';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const StyledMenuIcon = styled(MenuIcon)`
    color: white;
`;
const StyledLink = styled(Link)`
    text-decoration: none;
    color: black;
`;
const StyledSideList = styled.div`
    width: 250px;
`;
const Root = styled.div`
    flex-grow: 1;
`;
const StyledProfileText = styled.div`
    font-size:15px;
    padding-bottom:7px;
    padding-top:5px;
    padding-left:15px;
    padding-right:10px;
`;
const StyledProfileNameText = styled.div`
    font-size:20px;
    padding-top:5px;
    padding-bottom:5px;
    padding-left:15px;
    padding-right:10px;
    font-weight: bold;
`;

interface Props {
    title: string,
    bottom?: boolean,
    langButt?: JSX.Element,
}

interface MenuItem {
    name: string,
    routeName: string,
    icon: JSX.Element
}

const AboutUsMenuItems: Array<MenuItem> = [
    { name: "contactUs", routeName: "/contactus", icon: <Email color="primary" /> },
    { name: "faq", routeName: "/faq", icon: <LiveHelp color="primary" /> },
    { name: "tutorials", routeName: "/tutorials", icon: <LibraryBooks color="primary" /> },
];

const AccountSettings: Array<MenuItem> = [
    { name: "passwordSetting", routeName: "/settings/changepassword", icon: <Lock color="primary" /> },
];

const AdminMenuItems: Array<MenuItem> = [
    { name: "accounts", routeName: "/accounts", icon: <SupervisorAccount style={{ color: "#239139" }} /> },
    { name: "approveDemo", routeName: "/approvedemo", icon: <Assignment style={{ color: "#239139" }} /> },
    { name: "discover", routeName: "/discover", icon: <Explore style={{ color: "#239139" }} /> }
];

export default function SimpleAppBar(props: Props) {

    const theme = useTheme();
    const queryParams = useQueryParams();
    const smBreakPoint = useMediaQuery(theme.breakpoints.up('sm'));

    const dispatch = useDispatch();

    const [generalMenuItems, setGeneralMenuItems] = useState<Array<MenuItem>>();

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const open = Boolean(anchorEl);

    const [loggedInUser, setLoggedInUser] = useState<firebaseUser | null>();

    const userRole = useSelector<AppState, RoleType | null>(state => state.userRole);

    const userValidities = useSelector<AppState, UserValidity[] | null>(state => state.userValidities);

    useEffect(() => {
        firebaseAuth.onAuthStateChanged((user) => {
            if (user) {
                dispatch(loggedIn(user));
                setLoggedInUser(user);
            } else {
                // console.log("no user");
                setLoggedInUser(null);
                dispatch(loggedOut());
                dispatch(clearUserProfiles());
                dispatch(clearUserRole());
            }
        });
    }, [])

    useEffect(() => {
        const _getUserRole = async () => {
            if (loggedInUser !== undefined && loggedInUser !== null) {
                const role = await getUserRole(loggedInUser.uid);
                dispatch(setUserRole(role.role));
            }
        }
        if (userRole === "unknown" || userRole === null) {
            _getUserRole();
        }
    }, [loggedInUser])

    const valid_until = () => {
        if (loggedInUser && userValidities) {
            const thisUserValidity = userValidities.find((user) => user.uid === loggedInUser.uid)
            return moment.utc(thisUserValidity?.valid_till).local().format('DD/MM/YYYY')
        }
        else {
            return t("appbar.failed_to_load")
        }
    }

    useEffect(() => {
        let menuItem = [];
        if (userRole !== null) {
            menuItem.push({ name: "home", routeName: "/", icon: <Home color="primary" /> });
            menuItem.push({ name: "users", routeName: "/users", icon: <AccountBox color="primary" /> });
            if (!isTrialAccount(userRole)) {
                menuItem.push({ name: "reports", routeName: "/report", icon: <Assessment color="primary" /> });
            }
            if (userRole === "center" || userRole === "trial_center") {
                menuItem.push({ name: "announcements", routeName: "/announcement", icon: <Announcement color="primary" /> });
            }
            // menuItem.push({ name: "遊戲報告", routeName: "/reports/game" });
            // menuItem.push({ name: "任務報告", routeName: "/reports/mission" });
            setGeneralMenuItems(menuItem);
        }
    }, [loggedInUser, userRole])

    const sideList = () => (
        <StyledSideList
          role="presentation"
          onClick={toggleLeftMenu(false)}
          onKeyDown={toggleLeftMenu(false)}
          style={{ padding: 0 }}
        >
            <StyledLink key="homeButton" to={"/"}  
                style={{ 
                    backgroundColor: '#D2E5D4', 
                    justifyContent: 'center',
                    display: 'flex',
                    paddingTop: 12,
                    paddingBottom: 12,
                }}
            >
                <img 
                    src={logo_menu} 
                    style={{ display: 'block', height: 45, width: 90, marginRight: 15 }}
                    alt="MedMindLogo"
                />
                <span style={{ color: "#093a4f", fontSize: "20px", fontWeight: "bold" }}>
                    {/* 腦有記
                    <br/>
                    網上綜合系統 */}
                    {t("appbar.systemNameTop")}
                    <br/>
                    {t("appbar.systemNameBot")}
                </span>
            </StyledLink>
            <Divider style={{ backgroundColor: '#043a4f' }} />
            <Scrollbars 
                style={{
                    width: 250, 
                    // height: "calc(85% - 51px)"
                }}
                autoHeight
                autoHeightMax={"auto"}
            >
                {
                    (AdminMenuItems !== undefined && userRole === "admin") &&
                    (
                        <>
                        <List disablePadding>
                            <ListSubheader
                                key="adminTool"
                                style={{
                                    background: "linear-gradient(0deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.75) 50%, rgba(255,255,255,1) 100%)",
                                    marginRight: 10,
                                    color: "#239139",
                                    fontWeight: "bold",
                                    fontSize: "1.3rem",
                                    marginTop: 15
                                }}
                            >
                                {t("appbar.managerTools")}
                            </ListSubheader>
                            {AdminMenuItems.map((item, index) => (
                                <StyledLink key={index} to={item.routeName}>
                                    <ListItemButton style={{ paddingTop: 0, paddingBottom: 0 }}>
                                        <ListItemIcon>{item.icon}</ListItemIcon>
                                        <ListItemText style={{ color: "#239139", lineHeight: 1 }} primary={t(`appbar.${item.name}` as unknown as TemplateStringsArray)} />
                                    </ListItemButton >
                                </StyledLink>
                            ))}
                        </List>
                        <Divider style={{ marginTop: 10 }} />
                        </>
                    )
                }
                {
                    generalMenuItems !== undefined &&
                    <List disablePadding>
                        <ListSubheader 
                            key="dashboard"
                            style={{ 
                                background: "linear-gradient(0deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.75) 50%, rgba(255,255,255,1) 100%)", 
                                marginRight: 10, 
                                color: "#093a4f", 
                                fontWeight: "bold", 
                                fontSize: "1.3rem",
                            }}
                        >
                            {t("appbar.dashboard")}
                        </ListSubheader>
                        {generalMenuItems.map((item, index) => (
                            <StyledLink key={index} to={item.routeName}>
                                <ListItemButton style={{ paddingTop: 0, paddingBottom: 0, width: "auto"}}>
                                    <ListItemIcon>{item.icon}</ListItemIcon>
                                    <ListItemText style={{ color: "#093a4f", lineHeight: 1 }} primary={t(`appbar.${item.name}` as unknown as TemplateStringsArray)} />
                                </ListItemButton >
                            </StyledLink>
                        ))}
                    </List>
                }
                <Divider style={{ marginTop: 10 }} />
                <ListSubheader 
                    key="relatedResource"
                    style={{ 
                        background: "linear-gradient(0deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.75) 50%, rgba(255,255,255,1) 100%)", 
                        marginRight: 10, 
                        color: "#093a4f", 
                        fontWeight: "bold", 
                        fontSize: "1.3rem",
                    }}
                >
                    {t("appbar.relatedResources")}
                </ListSubheader>
                {
                    AboutUsMenuItems.map((item, index) => ([
                        <StyledLink key={index} to={item.routeName}>
                            <ListItemButton style={{ paddingTop: 0, paddingBottom: 0 }}>
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                <ListItemText style={{ color: "#093a4f", lineHeight: 1 }} primary={t(`appbar.${item.name}` as unknown as TemplateStringsArray)} />
                            </ListItemButton>
                        </StyledLink>,
                    ]))
                }
                <Divider style={{ marginTop: 10 }} />
                <ListSubheader 
                    key="accountSetting"
                    style={{ 
                        background: "linear-gradient(0deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.75) 50%, rgba(255,255,255,1) 100%)", 
                        marginRight: 10, 
                        color: "#093a4f", 
                        fontWeight: "bold", 
                        fontSize: "1.3rem",
                    }}
                >
                    {t("appbar.accountSetting")}
                </ListSubheader>
                {
                    AccountSettings.map((item, index) => ([
                        <StyledLink key={index} to={item.routeName}>
                            <ListItemButton>
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                <ListItemText style={{ color: "#093a4f", lineHeight: 1 }} primary={t(`appbar.${item.name}` as unknown as TemplateStringsArray)} />
                            </ListItemButton>
                        </StyledLink>,
                    ]))
                }
                <Divider style={{ marginTop: 10 }} />
            </Scrollbars>

            <ListItemButton key="exit" onClick={logout} style={{ display: "flex", bottom: 0, width: "100%" }} >
                <ListItemIcon>
                    <ExitToApp style={{ color: "#e02828" }} />
                </ListItemIcon>
                <ListItemText style={{ color: "#e02828" }} primary={t("appbar.logout")}/>
            </ListItemButton >
        </StyledSideList>
    );

    const logout = () => { firebaseAuth.signOut(); }

    const [isLeftMenuOpen, setisLeftMenuOpen] = React.useState<boolean>(false);

    const toggleLeftMenu = (open: boolean) => () => {
        setisLeftMenuOpen(open);
    };

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleProfileMenuClose = () => {
        setAnchorEl(null);
    };

    const redirectUrl = () => (
        <Navigate
            to={{
                pathname: `/login`,
            }}
            state={{
                from: window.location.pathname,
                token: queryParams.get("token") ? queryParams.get("token") : undefined,
                query_uid: queryParams.get("uid") ? queryParams.get("uid") : undefined
            }}
        />
    )

    const {t, i18n} = useTranslation();

    const top = () => {
        return (
            <>
            <IconButton key="menu" aria-label="menu" onClick={toggleLeftMenu(true)}>
                <StyledMenuIcon />
            </IconButton>
            <Typography key="title" variant="h6" color="inherit" sx={{ flexGrow: 1, width: "80%", textAlign: 'left' }}>
                {i18n.exists(`appbar.${props.title}`) ? t(`appbar.${props.title}` as unknown as TemplateStringsArray) : t(`common.404`)} 
                {(process.env.NODE_ENV !== 'production' 
                || process.env.REACT_APP_ENVIRONMENT === "test") 
                && <span style={{ color: "red" }}>(DEVELOPMENT SERVER)</span>}
            </Typography>
            {loggedInUser !== null && loggedInUser !== undefined &&
                <>
                {smBreakPoint && generalMenuItems !== undefined && generalMenuItems.map(x => {
                    if (x.name !== 'home')
                        return (
                            <ListItemButton key={x.routeName} href={x.routeName} style={{ display: "inline-block", alignContent: "center", whiteSpace: "nowrap", width: "250px" }} color='inherit'>
                                <Typography variant="subtitle1" color="inherit" align='center'>
                                    {t(`appbar.${x.name}` as unknown as TemplateStringsArray)}
                                </Typography>
                            </ListItemButton >
                        )
                })}
                <IconButton
                    key="accountButton"
                    aria-label="account of current user"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleProfileMenuOpen}
                    color="inherit"
                >
                    <AccountCircle fontSize={"large"} />
                </IconButton>
                <Menu
                    key="userMenu"
                    id="simple-menu"
                    anchorEl={anchorEl}
                    keepMounted
                    open={open}
                    onClose={handleProfileMenuClose}
                >
                    {loggedInUser.displayName !== null 
                    && [
                        <StyledProfileNameText key="userName">
                            {loggedInUser.displayName}
                        </StyledProfileNameText>
                    ]}
                    <StyledProfileText key="userEmail">{t("appbar.email")+ ": " + loggedInUser.email}</StyledProfileText>
                    <StyledProfileText key="userRole">{t("common.role")+ ": " + userRole}</StyledProfileText>
                    {/* {
                        (userRole !== "center_user" && userRole !== "admin" && userRole !== 'trial_center_user')?
                        <StyledProfileText key="userExpires">{t("appbar.accountExpireDate")+ ": " + valid_until()}</StyledProfileText>
                        : null
                    } */}
                </Menu>
                </>
            }
            {props.langButt?? ""}
            </> 
        )
    }

    const bottom = () => {
        return (
            <>
            <img src={logo_white} style={{ display: 'block', height: '7vh', width: '11vh', margin: 7 }} alt="MedMindLogo"/>
            {smBreakPoint &&
            <div>
                {
                    AboutUsMenuItems.map(x => (
                        <ListItemButton key={x.name} href={x.routeName} color='inherit' style={{ display:"inline-block", alignContent: "center", whiteSpace: "nowrap", width: "200px" }}>
                            <Typography variant="subtitle1" color="inherit" align='center'>
                                {t(`appbar.${x.name}` as unknown as TemplateStringsArray)}
                            </Typography>
                        </ListItemButton >
                    ))
                }
            </div>
            }
            </>
        )
    }

    return (
        <Root>
            <AppBar position='fixed' style={{ marginTop: 0, zIndex: 999}}>
                <Toolbar >
                    {top()}
                </Toolbar>
            </AppBar>
            {
                props.bottom && (
                    <AppBar position='fixed' style={{ marginTop: '92vh', zIndex: 999}}>
                        <Toolbar >
                            {bottom()}
                        </Toolbar>
                    </AppBar>
                )
            }
            <Drawer anchor="left" open={isLeftMenuOpen} onClose={toggleLeftMenu(false)}>
                {sideList()}
            </Drawer>
            {loggedInUser === null && redirectUrl()}
        </Root >
    );
}